/**
 * ActivationBlocker.jsx
 * 
 * Phase 3 - Activation Hard Gates UI Component
 * 
 * Displays missing requirements that block agent activation with actionable
 * "Fix Now" buttons that deep-link to the relevant wizard step or editor section.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BACKEND_URL } from '../config';
import { useOrganization } from '../context/OrganizationContext';
import { fetchWithOrg } from '../utils/api';

import {
  ArrowSquareOut,
  Calendar,
  ChatText,
  CheckCircle,
  GearSix,
  Key,
  MagicWand,
  Microphone,
  Phone,
  Warning,
  X
} from '@phosphor-icons/react';
// Map deep_link paths to icons and labels
const REQUIREMENT_CONFIG = {
  'wizard_completed': { icon: MagicWand, label: 'Complete Setup Wizard', color: 'purple' },
  'voice_config.enabled': { icon: Microphone, label: 'Enable Voice Config', color: 'blue' },
  'voice_config.stt_provider': { icon: Microphone, label: 'Configure Speech-to-Text', color: 'blue' },
  'voice_config.tts_provider': { icon: Microphone, label: 'Configure Text-to-Speech', color: 'blue' },
  'voice_config.tts_voice_id': { icon: Microphone, label: 'Select Voice', color: 'blue' },
  'voice_config.call_greeting': { icon: ChatText, label: 'Add Opening Message', color: 'green' },
  'twilio_credentials': { icon: Phone, label: 'Connect Telephony Provider (Twilio or Telnyx)', color: 'red' },
  'telephony_provider': { icon: Phone, label: 'Connect Telephony Provider (Twilio or Telnyx)', color: 'red' },
  'calendar_config': { icon: Calendar, label: 'Connect Calendar', color: 'amber' },
  'calendar_config.services': { icon: GearSix, label: 'Add Services', color: 'amber' },
  'sms_config.twilio': { icon: Phone, label: 'Configure SMS', color: 'teal' },
  'llm_key': { icon: Key, label: 'Add LLM API Key', color: 'gray' },
  'integration_auth': { icon: GearSix, label: 'Connect Integrations', color: 'amber' },
};

// Get icon component for a requirement
const getRequirementIcon = (field) => {
  return REQUIREMENT_CONFIG[field]?.icon || Warning;
};

// Get color class for a requirement
const getRequirementColor = (field) => {
  const color = REQUIREMENT_CONFIG[field]?.color || 'gray';
  return {
    bg: `bg-${color}-500/10`,
    border: `border-${color}-500/30`,
    text: `text-${color}-400`,
    hover: `hover:bg-${color}-500/20`
  };
};

/**
 * ActivationBlocker Component
 * 
 * Props:
 * - agentId: string - The agent ID to check activation status for
 * - onActivationReady: function - Callback when agent can be activated
 * - compact: boolean - Show compact version for inline use
 * - showActivateButton: boolean - Show the activate button when ready
 * - onActivate: function - Callback to trigger activation
 */
const ActivationBlocker = ({ 
  agentId, 
  onActivationReady,
  compact = false,
  showActivateButton = true,
  onActivate
}) => {
  const navigate = useNavigate();
  const { activeOrganization } = useOrganization();
  
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activating, setActivating] = useState(false);
  const [error, setError] = useState(null);
  
  // Fetch activation status
  useEffect(() => {
    if (agentId && activeOrganization) {
      fetchActivationStatus();
    }
  }, [agentId, activeOrganization]);
  
  const fetchActivationStatus = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/agents/${agentId}/activation-status`,
        { credentials: 'include' },
        activeOrganization?.organization_id
      );
      
      if (response.ok) {
        const data = await response.json();
        setStatus(data);
        
        // Notify parent if agent can be activated
        if (data.can_activate && onActivationReady) {
          onActivationReady(true);
        }
      } else {
        const errData = await response.json().catch(() => ({}));
        setError(errData.detail || 'Failed to check activation status');
      }
    } catch (err) {
      console.error('Error fetching activation status:', err);
      setError('Failed to check activation status');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle "Fix Now" click - navigate to appropriate location
  const handleFixNow = (requirement) => {
    const deepLink = requirement.deep_link;
    
    // Map deep_link to actual routes
    if (deepLink?.includes('/wizard')) {
      // Navigate to wizard
      navigate(`/partner-pro/ai-workforce/agents/${agentId}/wizard`);
    } else if (deepLink?.includes('/editor')) {
      // Navigate to editor with section
      navigate(`/partner-pro/ai-workforce/agents/${agentId}/edit`);
    } else if (deepLink?.includes('/settings')) {
      // Handle settings navigation
      // Fix legacy deep_links that used /settings/llm
      if (deepLink === '/settings/llm' || deepLink.includes('llm') || deepLink.includes('key')) {
        navigate('/settings?tab=ai');
      } else {
        navigate(deepLink);
      }
    } else {
      // Default: go to wizard for voice agents, editor otherwise
      if (status?.capability_profile?.includes('voice')) {
        navigate(`/partner-pro/ai-workforce/agents/${agentId}/wizard`);
      } else {
        navigate(`/partner-pro/ai-workforce/agents/${agentId}/edit`);
      }
    }
  };
  
  // Handle activation
  const handleActivate = async () => {
    setActivating(true);
    setError(null);
    
    try {
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/agents/${agentId}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ status: 'active' })
        },
        activeOrganization?.organization_id
      );
      
      if (response.ok) {
        if (onActivate) {
          onActivate();
        }
        // Refresh status
        fetchActivationStatus();
      } else {
        const errData = await response.json().catch(() => ({}));
        // Handle structured error with missing requirements
        if (errData.detail?.missing_requirements) {
          setStatus({
            can_activate: false,
            missing_requirements: errData.detail.missing_requirements,
            blocking_reasons: errData.detail.blocking_reasons,
            capability_profile: errData.detail.capability_profile
          });
        } else {
          setError(errData.detail?.message || errData.detail || 'Failed to activate agent');
        }
      }
    } catch (err) {
      console.error('Error activating agent:', err);
      setError('Failed to activate agent');
    } finally {
      setActivating(false);
    }
  };
  
  // Loading state
  if (loading) {
    return (
      <div className={`${compact ? 'p-3' : 'p-4'} bg-[rgba(255,255,255,0.02)] rounded-lg border border-[rgba(255,255,255,0.1)]`}>
        <div className="animate-pulse flex items-center gap-3">
          <div className="w-8 h-8 bg-gray-700 rounded-lg"></div>
          <div className="flex-1">
            <div className="h-4 bg-gray-700 rounded w-1/2 mb-2"></div>
            <div className="h-3 bg-gray-700 rounded w-3/4"></div>
          </div>
        </div>
      </div>
    );
  }
  
  // Error state - show helpful message for voice agents
  if (error) {
    return (
      <div className={`${compact ? 'p-3' : 'p-4'} bg-amber-500/10 rounded-lg border border-amber-500/30`}>
        <div className="flex items-start gap-3">
          <Warning size={20} className="text-amber-400 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-medium text-amber-400">Telephony Required for Activation</h4>
            <p className="text-sm text-amber-400/80 mt-1">
              This voice agent requires a telephony provider (Twilio or Telnyx) to be connected before it can be activated.
            </p>
            <button
              onClick={() => navigate(`/partner-pro/ai-workforce/agents/${agentId}/wizard`)}
              className="mt-3 text-sm text-[#6366F1] hover:underline flex items-center gap-1"
            >
              <MagicWand size={14} />
              Configure in Setup Wizard
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  // No status
  if (!status) return null;
  
  // Agent can be activated
  if (status.can_activate) {
    return (
      <div className={`${compact ? 'p-3' : 'p-4'} bg-green-500/10 rounded-lg border border-green-500/30`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
              <CheckCircle size={20} className="text-green-400" />
            </div>
            <div>
              <h4 className="font-medium text-white">Ready to Activate</h4>
              <p className="text-sm text-gray-400">All requirements met</p>
            </div>
          </div>
          
          {showActivateButton && (
            <button
              onClick={handleActivate}
              disabled={activating}
              className="px-4 py-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
            >
              {activating ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Activating...
                </>
              ) : (
                <>
                  <CheckCircle size={16} />
                  Activate Agent
                </>
              )}
            </button>
          )}
        </div>
      </div>
    );
  }
  
  // Agent has missing requirements
  const missingRequirements = status.missing_requirements || [];
  
  return (
    <div className={`${compact ? '' : 'p-4'} bg-amber-500/10 rounded-lg border border-amber-500/30`}>
      {/* Header */}
      <div className={`flex items-center gap-3 ${compact ? 'p-3' : 'mb-4'}`}>
        <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
          <Warning size={20} className="text-amber-400" />
        </div>
        <div>
          <h4 className="font-medium text-white">Activation Blocked</h4>
          <p className="text-sm text-gray-400">
            {missingRequirements.length} requirement{missingRequirements.length !== 1 ? 's' : ''} missing
          </p>
        </div>
      </div>
      
      {/* Requirements List */}
      {!compact && (
        <div className="space-y-2">
          {missingRequirements.map((req, idx) => {
            const IconComponent = getRequirementIcon(req.field);
            const colors = getRequirementColor(req.field);
            
            return (
              <div
                key={idx}
                className={`p-3 rounded-lg border flex items-center justify-between ${colors.bg} ${colors.border}`}
              >
                <div className="flex items-center gap-3">
                  <IconComponent size={18} className={colors.text} />
                  <span className="text-sm text-white">{req.message}</span>
                </div>
                
                <button
                  onClick={() => handleFixNow(req)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-1.5 ${colors.text} ${colors.hover} border ${colors.border}`}
                >
                  Fix Now
                  <ArrowSquareOut size={14} />
                </button>
              </div>
            );
          })}
        </div>
      )}
      
      {/* Compact: Show "Fix All" button */}
      {compact && missingRequirements.length > 0 && (
        <div className="px-3 pb-3">
          <button
            onClick={() => handleFixNow(missingRequirements[0])}
            className="w-full px-3 py-2 bg-amber-500/20 border border-amber-500/30 text-amber-400 rounded-lg text-sm font-medium hover:bg-amber-500/30 flex items-center justify-center gap-2"
          >
            <MagicWand size={16} />
            Complete Setup
          </button>
        </div>
      )}
    </div>
  );
};

export default ActivationBlocker;
